import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CreditCard, Info, Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { usePermissions } from "@/hooks/usePermissions";
import { usePlans } from "@/hooks/usePlans";
import { usePerUseActivationFee } from "@/hooks/usePerUseCardStatus";
import { useSubscriptionPreapproval } from "@/hooks/useSubscriptionPreapproval";
import { supabase } from "@/lib/supabase";
import { canPay } from "@/lib/billing-payer";
import { formatCop } from "@/lib/credits";
import { resolveCurrentPlan } from "@/lib/current-plan";
import { normalizePayerEmail, payerEmailError } from "@/lib/mercadopago-payer-email";
import { deadPendingExplanation, deadPendingReason } from "@/lib/pending-authorization";

export const CARD_AUTHORIZATION_DIALOG_TITLE = "Asociar tarjeta";
export const CARD_AUTHORIZATION_CONFIRM_LABEL = "Autorizar en MercadoPago";
export const CARD_AUTHORIZATION_PLAN_ERROR = "No pudimos cargar tu plan. Cierra esta ventana e intenta de nuevo.";
export const CARD_AUTHORIZATION_EXISTS_ERROR = "Ya hay una suscripción activa. Cancélala primero desde la sección Suscripción.";
export const CARD_AUTHORIZATION_GENERIC_ERROR = "Error al conectar con el servicio de pagos";
export const CARD_AUTHORIZATION_RESTART_ERROR = "No se pudo cancelar el registro pendiente. Intenta de nuevo.";

export function cardAuthorizationReferenceNotice(activationFee: number): string {
  return `Asociar la tarjeta no genera ningún cobro ahora: Mercado Pago te mostrará ${formatCop(activationFee)} COP solo como valor de referencia. Después del cierre de cada ciclo se debita lo que consumiste en él; si no consumiste nada, no se debita.`;
}

export interface CardAuthorizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string | null;
  planId?: string | null;
  onChanged?: () => void;
}

export function CardAuthorizationDialog({ open, onOpenChange, orgId, planId, onChanged }: CardAuthorizationDialogProps) {
  const { userRole } = useAuth();
  const brand = useBrand();
  if (brand.id !== "flow" || !canPay(userRole)) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" data-testid="card-authorization-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" aria-hidden="true" />
            {CARD_AUTHORIZATION_DIALOG_TITLE}
          </DialogTitle>
          <DialogDescription>
            Mercado Pago te pedirá los datos de la tarjeta. No necesitas cuenta de Mercado Pago, solo una tarjeta de crédito.
          </DialogDescription>
        </DialogHeader>
        <CardAuthorizationBody orgId={orgId} planId={planId} onClose={() => onOpenChange(false)} onChanged={onChanged} />
      </DialogContent>
    </Dialog>
  );
}

function useOrgPlanId(orgId: string | null, given: string | null | undefined) {
  const resolves = given === undefined || given === null;
  const { plans, loading: plansLoading } = usePlans(true);
  const [orgPlan, setOrgPlan] = useState<{ orgId: string; plan: string | null } | null>(null);

  useEffect(() => {
    if (!resolves || !orgId) return;
    let mounted = true;
    void (async () => {
      const { data } = await supabase.from("organizations").select("plan").eq("id", orgId).maybeSingle();
      if (mounted) setOrgPlan({ orgId, plan: (data as { plan: string | null } | null)?.plan ?? null });
    })();
    return () => { mounted = false; };
  }, [resolves, orgId]);

  const resolved = useMemo(() => {
    if (!resolves || !orgId || orgPlan?.orgId !== orgId || plansLoading) return null;
    return resolveCurrentPlan(plans, orgPlan.plan, orgId)?.id ?? null;
  }, [resolves, orgId, orgPlan, plans, plansLoading]);

  if (!resolves) return { planId: given, loading: false };
  return { planId: resolved, loading: !!orgId && (plansLoading || orgPlan?.orgId !== orgId) };
}

interface CardAuthorizationBodyProps {
  orgId: string | null;
  planId?: string | null;
  onClose: () => void;
  onChanged?: () => void;
}

function CardAuthorizationBody({ orgId, planId: givenPlanId, onClose, onChanged }: CardAuthorizationBodyProps) {
  const { user, agent } = useAuth();
  const { can, isLoading: permsLoading, isError: permsError } = usePermissions();
  const canChoosePayer = !permsLoading && !permsError && can("settings.account");
  const ownEmail = agent?.email || user?.email || "";
  const activationFee = usePerUseActivationFee(true);
  const { preapproval, loading: preapprovalLoading, startAuthorize, cancel } = useSubscriptionPreapproval(orgId);
  const { planId, loading: planLoading } = useOrgPlanId(orgId, givenPlanId);
  const [email, setEmail] = useState(ownEmail);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => { setEmail(ownEmail); }, [ownEmail]);

  const capture = preapproval?.mode === "per_use_card_capture" ? preapproval : null;
  const deadReason = deadPendingReason(capture);

  const handleConfirm = async () => {
    setError(null);
    let payerEmail: string | undefined;
    if (canChoosePayer) {
      const invalid = payerEmailError(email);
      if (invalid) {
        setEmailError(invalid);
        return;
      }
      payerEmail = normalizePayerEmail(email);
    }
    if (!planId) {
      setError(CARD_AUTHORIZATION_PLAN_ERROR);
      return;
    }
    setSubmitting(true);
    try {
      const { init_point } = await startAuthorize(planId, payerEmail, "per_use_card_capture");
      window.location.href = init_point;
    } catch (err) {
      const failure = err as { message?: string; code?: string | null };
      const message = failure?.message || "";
      setError(failure?.code === "preapproval_exists" || message.includes("preapproval_exists")
        ? CARD_AUTHORIZATION_EXISTS_ERROR
        : message || CARD_AUTHORIZATION_GENERIC_ERROR);
      setSubmitting(false);
    }
  };

  const handleRestart = async () => {
    setError(null);
    setRestarting(true);
    try {
      await cancel();
      onChanged?.();
    } catch {
      setError(CARD_AUTHORIZATION_RESTART_ERROR);
    } finally {
      setRestarting(false);
    }
  };

  const errorNotice = error && (
    <p role="alert" data-testid="card-authorization-error" className="rounded-lg border border-destructive/30 bg-destructive/10 p-2.5 text-xs font-medium text-destructive">
      {error}
    </p>
  );

  if (preapprovalLoading || planLoading) {
    return (
      <DialogBody>
        <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="card-authorization-loading">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Cargando...
        </p>
      </DialogBody>
    );
  }

  if (capture && capture.status === "pending") {
    return (
      <>
        <DialogBody>
          <p className="text-sm" data-testid="card-authorization-pending">
            {deadReason
              ? `${deadPendingExplanation(deadReason)} Empieza de nuevo para registrar la tarjeta.`
              : "Ya empezaste a asociar una tarjeta. Si no terminaste la autorización en Mercado Pago, puedes continuarla o empezar de nuevo."}
          </p>
          {errorNotice}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" className="h-11 sm:h-[32px] gap-1.5" onClick={handleRestart} disabled={restarting}>
            {restarting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <RotateCcw className="h-4 w-4" aria-hidden="true" />}
            {restarting ? "Cancelando..." : "Empezar de nuevo"}
          </Button>
          {capture.init_point && !deadReason && (
            <Button asChild className="h-11 sm:h-[32px] gap-1.5">
              <a href={capture.init_point} aria-disabled={restarting || undefined}>
                <ArrowRight className="h-4 w-4" aria-hidden="true" /> Continuar en MercadoPago
              </a>
            </Button>
          )}
        </DialogFooter>
      </>
    );
  }

  if (capture) {
    return (
      <>
        <DialogBody>
          <p className="text-sm" data-testid="card-authorization-processing">
            Tu tarjeta está siendo registrada. Mercado Pago confirmará la autorización en unos minutos y el débito automático quedará activo. No se hace ningún cobro ahora.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" className="h-11 sm:h-[32px]" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <form
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void handleConfirm();
      }}
      className="contents"
    >
      <DialogBody>
        {canChoosePayer ? (
          <div className="space-y-2">
            <Label htmlFor="card-reg-email" className="text-sm">Correo del titular de la tarjeta en Mercado Pago</Label>
            <Input
              id="card-reg-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="correo@ejemplo.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (emailError) setEmailError(null);
              }}
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? "card-reg-email-help card-reg-email-error" : "card-reg-email-help"}
              disabled={submitting}
              className="h-11 sm:h-10"
            />
            {emailError && (
              <p id="card-reg-email-error" role="alert" className="text-xs font-medium text-destructive">
                {emailError}
              </p>
            )}
            <p id="card-reg-email-help" className="text-xs text-muted-foreground" data-testid="card-reg-email-help">
              Mercado Pago te pedirá el mismo correo al confirmar. Si paga otra persona o el área de pagos de tu empresa, escribe el correo de esa persona.
            </p>
          </div>
        ) : (
          <p className="text-sm" data-testid="card-reg-own-email">
            {ownEmail
              ? <>Usaremos tu correo <strong className="break-all">{ownEmail}</strong>. Mercado Pago te pedirá el mismo correo al confirmar.</>
              : "Mercado Pago te pedirá tu correo al confirmar."}
          </p>
        )}
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-2.5 dark:border-blue-900 dark:bg-blue-950/40">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <p className="text-xs text-blue-800 dark:text-blue-300" data-testid="card-authorization-reference">
            {cardAuthorizationReferenceNotice(activationFee)}
          </p>
        </div>
        {errorNotice}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" className="h-11 sm:h-[32px]" onClick={onClose} disabled={submitting}>Cancelar</Button>
        <Button type="submit" className="h-11 sm:h-[32px] gap-1.5" disabled={submitting}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <CreditCard className="h-4 w-4" aria-hidden="true" />}
          {submitting ? "Redirigiendo..." : CARD_AUTHORIZATION_CONFIRM_LABEL}
        </Button>
      </DialogFooter>
    </form>
  );
}
